import { useUser } from '@application/authentication/use_user';
import { useFeatureFlag } from '@application/feature_flags/use_feature_flag';
import { withTranslation } from '@application/i18n/with_translation';
import { UniversalConfig } from '@application/universal_config.service';
import { validateNin } from '@application/validators';
import { Flex } from '@cellula/react';
import { TopBar } from '@component/top_bar.component';
import { RouterService } from '@cortex-shim/router.service';
import { Auth } from '@feature/auth/auth.component';
import { getCompletedInformationGathering } from '@feature/information-gathering/information-gathering-navigation';
import { useRespondentRepresentation } from '@feature/representation/use-respondent-representation';
import { getAbsoluteRoute } from '@module/get_absolute_route';
import { useQuery } from '@tanstack/react-query';
import { clientUnitsGetOptions, type UnitConfigApiModel, UserType } from '@visiba/api-vcs-client-v2';
import { useResolve } from '@visiba/instantiation';
import { NiN, NiNFormatter } from '@visiba/national-identity-number';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { CareNavigationService } from './care_navigation.service';
import { decideCareNavigationRespondent } from './care-navigation-respondent';
import { ScreenDone } from './screen_done';
import { ScreenError } from './screen_error';
import { ScreenLoading } from './screen_loading';
import { ScreenWhoIsRespondent } from './screen-who-is-respondent';

const RESPONDENT_SCREEN = 'who-is-respondent';

/** Keep the existing controller unmounted until its subject is known, including automatic starts. */
export function CareNavigationRespondentGate({
	children,
}: {
	children: (onAbort: () => void, onComplete: () => void, unitConfig: UnitConfigApiModel | undefined) => ReactNode;
}) {
	const service = useResolve(CareNavigationService);
	const config = useResolve(UniversalConfig);
	const router = useResolve(RouterService);
	const user = useUser();
	const location = useLocation();
	const featureUseRepresentatives = useFeatureFlag('PatientWeb.UseRepresentatives');
	const representation = useRespondentRepresentation({ resolveCurrentRepresentation: true });
	const [phase, setPhase] = useState<'loading' | 'select' | 'ready' | 'error'>('loading');
	const initialized = useRef(false);
	const [switchingRepresentation, setSwitchingRepresentation] = useState(false);
	const [selectedNin, setSelectedNin] = useState<string | null>(null);
	const [selectedDateOfBirth, setSelectedDateOfBirth] = useState<string | null>(null);
	const [completed, setCompleted] = useState(false);
	const countryCode = config.selectedCountryCode.value;
	const country = NiN.supportsCountryCode(countryCode) ? countryCode : 'SE';
	const isClient = user?.userType === UserType.Client;
	const routeSearch = new URLSearchParams(location.search);
	const urlNin = routeSearch.get('patientNin') || routeSearch.get('nin') || null;
	const needsUnitConfig = isClient && (featureUseRepresentatives || urlNin !== null);
	const unitConfig = useQuery({
		...clientUnitsGetOptions({ path: { unitId: config.selectedUnitId, platform: 'web' } }),
		enabled: needsUnitConfig,
	});
	const resolvedUnitConfig = unitConfig.data?.Data ?? undefined;
	const allowRepresentative = resolvedUnitConfig?.Offer?.AllowClientAgents ?? false;
	const onRespondentRoute = location.pathname.endsWith(`/${RESPONDENT_SCREEN}`);

	function goToScreen(screen: string, replace: boolean, preserveSubject: boolean) {
		const search = new URLSearchParams(window.location.search);
		if (preserveSubject) {
			// The selected subject now lives in the existing handoff storage. Do not re-import an old URL NIN.
			search.delete('patientNin');
			search.delete('nin');
			search.set('careNavigationSubjectContext', 'stored');
		}
		const path = getAbsoluteRoute([service.getRouteBase(), 'navigation', screen].filter(Boolean).join('/'));
		router.navigateByUrl(`${path}?${search}`, { replace, state: router.history.location.state });
	}

	function continueAs(nin: string | null, replace: boolean, preserveDateOfBirth = false, dateOfBirth: string | null = null) {
		if (!preserveDateOfBirth) service.setCurrentPatientDateOfBirth(dateOfBirth);
		service.setCurrentPatientNin(nin);
		setSelectedNin(nin);
		setSelectedDateOfBirth(dateOfBirth);
		goToScreen('reason-for-contact', replace, true);
		setPhase('ready');
	}

	useEffect(() => {
		if (initialized.current || !isClient || (needsUnitConfig && unitConfig.isPending) || representation.isLoadingRepresentablePatients)
			return;
		initialized.current = true;
		if (
			(needsUnitConfig && unitConfig.error) ||
			representation.representablePatientsError ||
			representation.hasUnresolvedRepresentation
		) {
			setPhase('error');
			return;
		}
		const decision = decideCareNavigationRespondent({
			featureUseRepresentatives,
			hasSubmission: getCompletedInformationGathering(router.history.location.state) !== null,
			isRepresenting: representation.currentlyRepresentedPatient !== null,
			preserveSubject: service.shouldPreserveSubjectContext(),
			storedNin: service.getCurrentPatientNin(),
			urlNin,
			allowRepresentative,
			hasRepresentablePatients: representation.representablePatients.length > 0,
		});
		if (decision.kind === 'conflict' || (decision.kind === 'continue' && decision.nin && !validateNin(country, decision.nin))) {
			setPhase('error');
		} else if (decision.kind === 'continue') {
			continueAs(
				decision.nin ? NiNFormatter.format(country, NiN.normalize(country, decision.nin.trim())) : null,
				true,
				decision.preserveDateOfBirth,
			);
		} else {
			service.setCurrentPatientNin(null);
			goToScreen(RESPONDENT_SCREEN, true, false);
			setPhase('select');
		}
	});

	function abort() {
		service.setCurrentPatientNin(null);
		window.location.href = getAbsoluteRoute([config.alias, service.getRouteBase()].filter(Boolean).join('/'));
	}

	if (completed && onRespondentRoute) return <ScreenDone onClose={abort} />;
	if (isClient && phase === 'ready' && !onRespondentRoute) return children(abort, () => setCompleted(true), resolvedUnitConfig);

	function renderScreen() {
		if (!isClient) return <Auth.Methods returnUrl={window.location.href} />;
		if (phase === 'error') return <ScreenError title='Unable to start care navigation' />;
		if (phase === 'loading') return <ScreenLoading />;
		return (
			<ScreenWhoIsRespondent
				country={country}
				allowRepresentative={allowRepresentative}
				representablePatients={representation.representablePatients}
				initialNin={selectedNin}
				initialDateOfBirth={selectedDateOfBirth}
				disabled={switchingRepresentation}
				onContinue={(nin, dateOfBirth) => continueAs(nin, false, false, dateOfBirth)}
				onRepresent={(id) => {
					if (!user?.remoteLoginScheme) return;
					service.setCurrentPatientNin(null);
					setSwitchingRepresentation(true);
					representation.startRepresentation(id);
				}}
			/>
		);
	}

	return (
		<Flex grow='1'>
			<TopBar.Content title={withTranslation('View.CreateNewMessage.Title')} onAbort={abort} />
			{renderScreen()}
		</Flex>
	);
}
